/**
 * Display formatters shared by every extranet screen.
 *
 * VEOS display rules (repeated in almost every fiche — see
 * `specs/extranet/endpoints.md` "Common display rules"):
 * - a `null` value renders nothing (empty string), never the text "null";
 * - ms timestamps render `JJ/MM/AAAA` « sans conversion de fuseau » — the
 *   timestamp is « déjà dans le fuseau horaire actuel », i.e. it is the UTC
 *   instant of a midnight in the VEOS (Paris) timezone. The fiche's own
 *   sample proves it: `WS2_EA_TAB_QUI` returns `DATDEB_QUI: 1770332400000`
 *   (`2026-02-05T23:00Z`) with `PERIODE: "06/02/2026 au 31/12/2026"`. The
 *   calendar date is therefore read in {@link VEOS_TIME_ZONE} — UTC getters
 *   would show the day before, and browser-local getters would shift it for
 *   users west of Paris;
 * - strings already formatted `DD/MM/YYYY` by the SQL are displayed as-is;
 * - amounts use 2 decimals + « € » (`1 800,00 €`).
 *
 * Every helper is null-safe and returns `''` for a missing / unparseable
 * value.
 */

/** A raw VEOS scalar as it may come back from the API. */
export type VeosScalar = string | number | null | undefined;

/**
 * Timezone VEOS dates are expressed in: a date-only value is the UTC instant
 * of midnight in this zone (see the file header for the evidence).
 */
export const VEOS_TIME_ZONE = 'Europe/Paris';

const EUR_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

const VEOS_DATE_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
  timeZone: VEOS_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Coerce a VEOS scalar to a finite number, or `null`.
 *
 * Accepts numbers and numeric strings (`"1800.00"`); `''`, `null`,
 * `undefined` and non-numeric strings yield `null`.
 */
export function toFiniteNumber(value: VeosScalar): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Format a VEOS Unix timestamp in milliseconds as `JJ/MM/AAAA` — the
 * calendar date in {@link VEOS_TIME_ZONE}, whatever the browser timezone.
 *
 * @example formatMsDate(1770332400000) // '06/02/2026' (2026-02-05T23:00Z)
 */
export function formatMsDate(ms: VeosScalar): string {
  const value = toFiniteNumber(ms);
  if (value === null) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = VEOS_DATE_FORMATTER.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')}/${part('month')}/${part('year')}`;
}

/**
 * Today's date `JJ/MM/AAAA` in {@link VEOS_TIME_ZONE} (the « date du jour »
 * VEOS expects, e.g. `creationDate` / `dateReception` of a piece upload),
 * whatever the browser timezone.
 *
 * @example todayDdMmYyyy(new Date('2026-10-28T23:30:00Z')) // '29/10/2026'
 */
export function todayDdMmYyyy(now: Date = new Date()): string {
  return formatMsDate(now.getTime());
}

/**
 * Pass through a date string the SQL already formatted (`DD/MM/YYYY`),
 * trimmed. `null` / blank ⇒ `''`.
 */
export function formatDdMmYyyy(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/**
 * Format any VEOS date value: a number is a ms timestamp
 * ({@link formatMsDate}), a string is already formatted
 * ({@link formatDdMmYyyy}).
 */
export function formatVeosDate(value: VeosScalar): string {
  if (typeof value === 'number') return formatMsDate(value);
  return formatDdMmYyyy(value);
}

/**
 * Format an amount in euros with two decimals: `1 800,00 €`.
 *
 * Uses `Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })`
 * — the same output as the DataList `currency` column type, so a card and a
 * table show identical amounts. `null` / non-numeric ⇒ `''`.
 */
export function formatEur(value: VeosScalar): string {
  const amount = toFiniteNumber(value);
  return amount === null ? '' : EUR_FORMATTER.format(amount);
}

/** Group a digit string by 3 from the left (`812456789` → `812 456 789`). */
function groupBy3(digits: string): string {
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
}

/**
 * Format a SIRET from its SIREN (9 digits) and NIC (5 digits):
 * `812 456 789 00021`.
 *
 * A SIREN that is not 9 digits is kept verbatim (trimmed). A missing NIC
 * yields the formatted SIREN only; a missing SIREN yields `''`.
 */
export function formatSiret(siren: VeosScalar, nic: VeosScalar): string {
  const sirenText = siren === null || siren === undefined ? '' : String(siren).trim();
  if (sirenText === '') return '';
  const formattedSiren = /^\d{9}$/.test(sirenText) ? groupBy3(sirenText) : sirenText;
  const nicText = nic === null || nic === undefined ? '' : String(nic).trim();
  return nicText === '' ? formattedSiren : `${formattedSiren} ${nicText}`;
}

/** The parts of a postal address as VEOS returns them. */
export interface AddressParts {
  /** Street lines (`ADRESSE1` / `ADRESSE2` / `ADRESSE3`), in order. */
  lines?: ReadonlyArray<string | null | undefined>;
  /** Postal code (`CP`). */
  postalCode?: VeosScalar;
  /** City (`VILLE`). */
  city?: string | null;
}

/**
 * Build a one-line postal address: the non-empty street lines, then
 * `CP VILLE`, joined with `, `.
 *
 * @example buildAddress({ lines: ['12 rue X', null], postalCode: '75001', city: 'PARIS' })
 *   // '12 rue X, 75001 PARIS'
 */
export function buildAddress(parts: AddressParts): string {
  const lines = (parts.lines ?? [])
    .map((line) => line?.trim() ?? '')
    .filter((line) => line !== '');
  const postalCode =
    parts.postalCode === null || parts.postalCode === undefined
      ? ''
      : String(parts.postalCode).trim();
  const locality = [postalCode, parts.city?.trim() ?? '']
    .filter((part) => part !== '')
    .join(' ');
  return [...lines, locality].filter((part) => part !== '').join(', ');
}

/**
 * `Prénom Nom` from VEOS name parts, skipping the missing ones.
 * Both missing ⇒ `''`.
 */
export function formatPersonName(
  prenom: string | null | undefined,
  nom: string | null | undefined,
): string {
  return [prenom?.trim() ?? '', nom?.trim() ?? '']
    .filter((part) => part !== '')
    .join(' ');
}

/** A VEOS text value for display: trimmed, `null` ⇒ `''`. */
export function formatText(value: VeosScalar): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}
